import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import React, { FC, ReactNode } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import brandGenLogo from "../../assets/images/logo_blue_black.svg";

const Root = styled(Box)({
  width: "100%",
  height: "100%",
  overflowY: "auto",
  boxSizing: "border-box",
});

const Inner = styled(Box)({
  width: "90%",
  maxWidth: 1130,
  minHeight: "100%",
  margin: "0 auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "24px 0",
  boxSizing: "border-box",
  "@media (max-width: 1024px)": {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: "32px 0 48px",
  },
});

const LeftPanel = styled(Box)({
  width: "50%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  paddingRight: 24,
  "@media (max-width: 1024px)": {
    width: "100%",
    maxWidth: 544,
    paddingRight: 0,
    marginBottom: 32,
    alignItems: "center",
    textAlign: "center",
  },
});

const RightPanel = styled(Box)({
  width: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "@media (max-width: 1024px)": {
    width: "100%",
  },
});

const Logo = styled("img")({
  width: 250,
  "@media (max-height: 820px)": {
    width: 200,
  },
  "@media (max-width: 768px)": {
    width: 180,
  },
  "@media (max-width: 425px)": {
    width: 140,
  },
});

const Heading = styled(Typography)({
  "&&": {
    marginTop: 32,
    fontSize: 56,
    fontWeight: 600,
    lineHeight: "69px",
  },
  "@media (max-height: 820px)": {
    "&&": {
      marginTop: 16,
      fontSize: 40,
      lineHeight: "48px",
    },
  },
  "@media (max-width: 1024px)": {
    "&&": {
      fontSize: 36,
      lineHeight: "44px",
      textAlign: "center",
    },
  },
  "@media (max-width: 768px)": {
    "&&": {
      fontSize: 28,
      lineHeight: "34px",
    },
  },
});

const Description = styled(Typography)({
  fontSize: 18,
  marginTop: 12,
  maxWidth: 377,
  lineHeight: "24px",
  "@media (max-height: 820px)": {
    fontSize: 16,
    marginTop: 8,
  },
  "@media (max-width: 1024px)": {
    textAlign: "center",
    margin: "12px auto 0",
  },
  "@media (max-width: 425px)": {
    fontSize: 16,
  },
});

const CardPaper = styled(Paper)(({ theme }: { theme: Theme }) => ({
  width: "100%",
  maxWidth: 544,
  padding: "28px 32px",
  borderRadius: 10,
  border: `1px solid ${theme.palette.brand.gray200}`,
  "@media (max-height: 820px)": {
    padding: "20px 24px",
  },
  "@media (max-width: 425px)": {
    padding: "20px 16px",
  },
}));

type BrandAuthLayoutProps = {
  children: ReactNode;
  heading?: string;
  description?: string;
};

const BrandAuthLayout: FC<BrandAuthLayoutProps> = ({
  children,
  heading = "Welcome Back",
  description = "Log in: to view your personalized dashboard, campaigns, and audiences.",
}) => {
  return (
    <Root>
      <Inner>
        <LeftPanel>
          <Logo src={brandGenLogo} alt="BrandGen Logo" />
          <Heading variant="h1">{heading}</Heading>
          <Description>{description}</Description>
        </LeftPanel>

        <RightPanel>
          <CardPaper elevation={0}>{children}</CardPaper>
        </RightPanel>
      </Inner>
    </Root>
  );
};

export default BrandAuthLayout;
